<template>
  <container-wrapper-full>
    <plan-heading
      v-if="!loading"
      :id="String(planData.id)"
      :plan-name="planData.name || ''"
      :apartment-name="planData.apartment_name"
      :apartment-id="planData.apartment_id"
      :can-edit="canRenamePlan"
      @submit-name="changeName">
      <template #status>
        <crm-plan-info
          v-if="plan"
          :plan="plan"
          :plan-data="planData"
          :is-merged="Boolean(planData.merged_from_plan_id)"
          :tags-list="tagsList"
          :can-change-assignees="canChangeAssignees"
          :can-change-ninja="canChangeNinja"
          :can-change-reviewer="canChangeReviewer"
          :options-tags="true"
          :attach-tags-to-plan="attachTagsToPlan"
          :unreal-rtx-preset="plan?.plan?.options?.unreal_rtx_preset"
          route-for-crm-plan="/crm/plans/"
          @change-ninja-success="loadData"
          @deadline-changed="loadData"
          @change-strategic-data="loadData" />
        <a-tag
          v-if="planData.apartment.is_paused"
          color="orange">
          Апартамент на паузе
        </a-tag>
      </template>

      <template #breadcrumbAside>
        <invalidation-info
          :invalidations="planData.invalidations"
          variant="breadcrumb" />
      </template>

      <template #actions>
        <actions-buttons
          :buttons="buttons"
          @button-click="onButtonClick" />
      </template>
    </plan-heading>

    <plan-main
      :plan="planData"
      :plan-id="planId"
      :plan-type="type"
      :service-plan-id="planData?.plan_id"
      :compare-data="compareCarousel"
      :enable-image-upload="true"
      :load-data="loadData"
      :expected-verification-comments="expectedVerificationComments"
      @verification-started="handleVerificationStarted">
      <template #after-controls>
        <a-row
          :gutter="[16, 16]"
          :style="{ marginTop: '16px' }">
          <a-col
            v-if="planId && canViewBranding"
            :span="24">
            <a-button
              :style="{ width: '100%' }"
              @click="isBrandingModalOpen = true">
              Брендинг планировки
              <a-tag
                color="#45A5FF"
                :style="{ marginLeft: '.5rem' }">
                New
              </a-tag>
            </a-button>
          </a-col>

          <a-col
            v-if="isGuardsVisible"
            :span="24">
            <a-button
              :style="{ width: '100%' }"
              @click="isGuardsModalOpen = true">
              Проверки планировок
              <a-tag
                color="#45A5FF"
                :style="{ marginLeft: '.5rem' }">
                New
              </a-tag>
            </a-button>
          </a-col>
        </a-row>
      </template>
    </plan-main>

    <a-modal
      v-model:open="isBrandingModalOpen"
      centered
      width="720px"
      :footer="null"
      title="Брендинг планировки">
      <plan-branding-section
        v-if="planId && canViewBranding"
        :plan-id="planId"
        :can-edit="true"
        :show-title="false" />
    </a-modal>

    <a-modal
      v-model:open="isGuardsModalOpen"
      centered
      width="720px"
      :footer="null"
      title="Проверки планировок">
      <plan-guard-bindings
        :owner-type="GuardOwnerType.CrmPlan"
        :owner-id="planId" />
      <p class="plan-page__guards-hint">
        Кроме привязанных здесь, к планировке применяются проверки её апартамента и заказчика-загрузчика.
      </p>
    </a-modal>

    <section
      v-if="adjustments?.length"
      style="margin: 1rem 0;">
      <a-divider>{{ getLocaleText('adjustments') }}</a-divider>

      <a-collapse
        allow-multiple
        style="width: 100%;">
        <a-collapse-panel
          v-for="(adjust, index) in adjustments"
          :key="adjust.id"
          :header="`Корректировка №${index + 1}`"
          style="width: 100%;">
          <div v-if="adjust?.description">
            <a-divider style="margin: 0">
              Описание
            </a-divider>
            <a-flex
              style="margin: 24px 0">
              <div
                :style="{ display: 'block', wordBreak: 'break-all', overflow: 'auto' }"
                :innerHTML="sanitizeHTML(parsedText(adjust.description))" />
            </a-flex>
          </div>

          <div v-if="adjust?.reference_paths?.length">
            <a-divider style="margin: 0">
              Прикрепленные файлы
            </a-divider>

            <image-viewer :images="adjust.reference_paths">
              <a-row :gutter="[16, 16]">
                <a-col
                  v-for="(path, idx) in adjust.reference_paths"
                  :key="idx"
                  :span="4"
                  :style="{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '140px',
                    textAlign: 'center'
                  }">
                  <template v-if="isImage(path)">
                    <img
                      :src="path"
                      :alt="`Корректировка ${index + 1}`"
                      :style="{
                        maxHeight: '100%',
                        width: 'auto',
                        objectFit: 'contain'
                      }"
                      @click.stop>
                  </template>
                  <template v-else>
                    <a-button
                      type="primary"
                      :href="path"
                      download
                      target="_blank"
                      :style="{
                        width: '100%'
                      }"
                      @click.stop>
                      Скачать {{ idx + 1 }}й файл ({{ getFileExtension(path) }})
                    </a-button>
                  </template>
                </a-col>
              </a-row>
            </image-viewer>
          </div>
        </a-collapse-panel>
      </a-collapse>
    </section>

    <a-skeleton
      v-if="loading"
      active
      :paragraph="{ rows: 7 }" />

    <section
      v-if="!loading && (planData.links?.svg_editor.jpg
        || planData.links?.svg_editor.svg
        || planData.links?.svg_editor.svg_layout
        || planData.links?.svg_editor.jpg_layout)"
      id="svg-editor-result">
      <a-divider type="horizontal" />
      <crm-plan-2d-results :links="planData?.links?.svg_editor" />
    </section>

    <section v-if="planData.apartment_id">
      <a-row :gutter="[16,16]">
        <a-col
          v-if="planData.apartment_id"
          :span="24">
          <loaded-files-list
            :files="planData.apartment_files"
            :files-sum="planData.apartment_files?.length"
            :loading="isSourcesArchiveCreating"
            @download="downloadSources" />
        </a-col>
      </a-row>
    </section>

    <plan-quality-log
      :crm-plan-id="planId"
      :type="type" />

    <a-row
      type="flex"
      align="top"
      :style="{ gap: '1rem' }">
      <plan-status-logs
        :id="planId"
        :type="type" />

      <plans-comments
        :id="planId"
        ref="planComments"
        :type="type"
        :auth="authUser"
        :is-verification-mode="isVerificationMode"
        :expected-verification-comments="expectedVerificationComments"
        :update-activity-logs="updateActivityLogs"
        @published-comment="loadData"
        @verification-completed="handleVerificationCompleted" />
    </a-row>

    <plan-revisions
      v-if="isPlanRevisionsVisible && !loading"
      :crm-plan-id="planId"
      :links="planData.links" />

    <plan-history
      ref="planHistoryRef"
      :crm-plan-id="planId"
      :plan-type="PlanActivityLogType.CrmPlan2d" />

    <change-ninja-modal
      :plan-id="planData.id"
      action="crm/changeCrmPlanNinja"
      @change-ninja-success="updateActivityLogs" />

    <change-manager-modal
      :plan-id="planData.id"
      action="crm/changeCrmPlanManager"
      @change-manager-success="updateActivityLogs" />

    <change-reviewer-modal
      :plan-id="planData.id"
      action="crm/changeCrmPlanReviewer"
      @change-reviewer-success="loadData" />

    <change-cutter-modal
      :plan-id="plan?.id"
      action="crm/changeCrmPlanCutter"
      @change-cutter-success="updateActivityLogs" />
  </container-wrapper-full>
</template>

<script setup>
import {
  computed, onMounted, onUnmounted, ref, watch, watchEffect,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { PlanType } from '@/enums/planType';

import { getStatusTag } from '@/enums/PlanStatusEnum';

import PlansComments from '@/Components/CRM/Plans/Comments.vue';
import PlanQualityLog from '@/Components/CRM/QualityReview/PlanQualityLog.vue';
import ContainerWrapperFull from '@/Components/Common/ContainerWrapperFull.vue';
import PlanHeading from '@/Components/Service/Plans/PlanHeadingWrapper.vue';
import PlanStatusLogs from '@/Components/CRM/Plans/StatusLogs.vue';
import ChangeNinjaModal from '@/Components/CRM/Plans/Modals/ChangeNinjaModal.vue';
import ChangeCutterModal from '@/Components/CRM/Plans/Modals/ChangeCutterModal.vue';
import ChangeManagerModal from '@/Components/CRM/Plans/Modals/ChangeManagerModal.vue';
import ChangeReviewerModal from '@/Components/CRM/Plans/Modals/ChangeReviewerModal.vue';
import { executeAction, getPlanActionsForUser } from '@/composables/planActions';
import CrmPlanInfo from '@/Components/CRM/Plans/CrmPlanInfo.vue';
import LoadedFilesList from '@/Components/CRM/Apartments/LoadedFilesList.vue';
import ActionsButtons from '@/Components/CRM/Plans/ActionsButtons.vue';
import PlanGuardBindings from '@/Components/Common/PlanGuardBindings.vue';
import getFlag from '@/helpers/getFlag';
import { GuardOwnerType } from '@/enums/guard';
import { getFilesAsNameAndUrl } from '@/utils/getFilesAsNameAndUrl';
import PlanMain from '@/Components/CRM/Plans/PlanMain.vue';
import { message } from 'ant-design-vue';
import { parsedText, sanitizeHTML } from '@/utils/htmlTextParser';
import ImageViewer from '@/Components/Common/ImageViewer.vue';
import localeStore from '@/store/locale';
import CrmPlan2dResults from '@/Components/CRM/Plans/CrmPlan2dResults/CrmPlan2dResults.vue';
import PlanHistory from '@/Components/PlanHistory.vue';
import PlanRevisions from '@/Components/PlanRevisions.vue';
import { PlanActivityLogType } from '@/enums/planActivityLogType';
import { ReplicateStatus } from '@/enums/replicateStatus';
import { api } from '@/api';
import { useCrmPlanState } from '@/composables/useCrmPlanState';
import PlanBrandingSection from '@/Components/CRM/Plans/PlanBrandingSection.vue';
import InvalidationInfo from '@/Components/Common/InvalidationInfo.vue';

const setPlanData = (data) => ({
  crm_tags: data.crm_tags,
  crmTagsModified: data.crm_tags.map((tag) => tag.id),
  id: data.id,
  name: data.name,
  status: getStatusTag(data.status),
  available_status_transitions: data.available_status_transitions,
  quality_review_iteration: data.quality_review_iteration,
  ninja: data.ninja,
  reviewer: data.reviewer,
  deadline_at: data.deadline_at,
  deadline_overridden: data.deadline_overridden,
  cutter: data.cutter,
  manager: data.manager,
  pictures: data.pictures,
  links: data.links,
  description: data.description,
  plan_id: data.plan_id,
  apartment_id: data.apartment?.id || null,
  apartment_name: data.apartment?.name || null,
  apartment_files: getFilesAsNameAndUrl(data.apartment?.files || []),
  apartment: {
    status: data.apartment?.status,
    is_paused: data.apartment?.is_paused,
    description: data.apartment?.description || null,
    integration_with_bitrix: data.apartment?.integration_with_bitrix,
    project_type: data.apartment?.project_type,
  },
  address: data.address,
  complex: data.complex,
  developer: data.developer,
  region: data.region,
  merged_from_plan_id: data.merged_from_plan_id,
  references: data.references,
  bitrix_leads: data?.bitrix_leads || [],
  invalidations: data.invalidations || data.plan?.invalidations || [],
});

const props = defineProps({
  id: {
    type: Number,
    default: () => null,
  },
});

const router = useRouter();
const route = useRoute();
const store = useStore();

const planData = ref({});

const planId = computed(() => Number(props.id || route.params.id));
const isGuardsVisible = getFlag('isGuardsVisible');
const isPlanRevisionsVisible = getFlag('isPlanRevisionsVisible');
const isBrandingModalOpen = ref(false);
const isGuardsModalOpen = ref(false);

const buttons = computed(() => getPlanActionsForUser(PlanType.Type2d, planData.value, store.state.auth.currentUser));

const loading = ref(true);

const samePlansVisible = ref(false);

const planHistoryRef = ref(null);

const plan = computed(() => store.state.crm.currentPlan);

const authUser = computed(() => store.state.auth.currentUser);

const canViewBranding = computed(() => store.getters['auth/hasPermission']('api.crm.plans.branding.show'));
const canRenamePlan = computed(() => store.getters['auth/hasPermission']('api.crm.plans.rename'));

const { getLocaleText } = localeStore();

const planComments = ref(null);
const isVerificationMode = ref(false);
const expectedVerificationComments = ref(0);

const replicationMessageShown = ref(false);
const isReplicationActive = ref(false);

const closeModal = () => {
  samePlansVisible.value = false;
};

const updateActivityLogs = () => {
  if (planHistoryRef.value) {
    planHistoryRef.value.loadActivityLogs();
  }
};

const verificationData = ref({
  expectedComments: 0,
  receivedComments: 0,
});

const handleVerificationStarted = (eventData) => {
  isVerificationMode.value = true;
  expectedVerificationComments.value = (eventData?.checklistsCount || 0) + (eventData?.hasCustomChecklist ? 1 : 0);
  updateActivityLogs();
};

const handleVerificationCompleted = () => {
  isVerificationMode.value = false;
  verificationData.value = { expectedComments: 0, receivedComments: 0 };
  updateActivityLogs();
};

// PlanHistory сам загружает логи в onMounted, поэтому первая загрузка страницы их не дёргает.
const isInitialLoad = ref(true);

const loadData = () => {
  loading.value = true;
  return store
    .dispatch('crm/getCrmPlanById', planId.value)
    .then((resp) => {
      planData.value = setPlanData(resp);

      if (isInitialLoad.value) {
        isInitialLoad.value = false;
      } else {
        updateActivityLogs();
      }
    })
    .catch((e) => {
      message.error(e.response?.data?.message || 'Ошибка загрузки данных');
    })
    .finally(() => (loading.value = false));
};

watch(
  planId,
  (newId) => {
    if (!newId) return;
    loadData();

    if (samePlansVisible.value) closeModal();
  },
  { immediate: true },
);

const replication = computed(() => plan.value?.plan?.additional?.replicate || null);

const FINISHED_TOAST_TTL_MS = 20 * 1000;

const isFinishedToastAllowed = (finishedAtIso) => {
  if (!finishedAtIso) return false;
  const ts = Date.parse(finishedAtIso);
  if (Number.isNaN(ts)) return false;
  return (Date.now() - ts) <= FINISHED_TOAST_TTL_MS;
};

const lastFinishedAtShown = ref(null);

const refreshPlanState = async (state, isCurrent) => {
  const { data } = await api.getCrmPlanById(planId.value, { silent: true, timeout: 10000 });
  if (!isCurrent()) return;
  store.commit('crm/setCurrentPlan', data);
  planData.value = setPlanData(data);
  updateActivityLogs();
};

useCrmPlanState(planId, refreshPlanState, () => !loading.value);

const resetReplicationFeedback = () => {
  message.destroy('copying');
  replicationMessageShown.value = false;
  isReplicationActive.value = false;
  lastFinishedAtShown.value = null;
};

watch(
  planId,
  (newId, oldId) => {
    if (!newId || !oldId || newId === oldId) return;
    resetReplicationFeedback();
  },
  { immediate: false },
);

watch(
  () => replication.value?.status,
  (status, prevStatus) => {
    const finishedAt = replication.value?.finished_at;
    const startedStatuses = new Set([ReplicateStatus.INITIATED, ReplicateStatus.STARTED]);

    if (!status || status === ReplicateStatus.NOT_INITIATED) return;

    if (startedStatuses.has(status) && !replicationMessageShown.value) {
      message.info('Началось копирование файлов. Некоторые файлы и сервисы могут быть недоступны');
      replicationMessageShown.value = true;
      isReplicationActive.value = true;

      message.loading({
        content: 'Копирование файлов...',
        key: 'copying',
        duration: 0,
      });

      return;
    }

    const shouldShowTerminalToast = isReplicationActive.value || startedStatuses.has(prevStatus);

    if (status === ReplicateStatus.FAILED) {
      message.destroy('copying');
      if (shouldShowTerminalToast) {
        message.error('Копирование файлов завершилось с ошибкой');
      }
      replicationMessageShown.value = false;
      isReplicationActive.value = false;
      return;
    }

    if (status === ReplicateStatus.FINISHED) {
      message.destroy('copying');

      if (!shouldShowTerminalToast) {
        replicationMessageShown.value = false;
        isReplicationActive.value = false;
        return;
      }

      if (!isFinishedToastAllowed(finishedAt)) {
        replicationMessageShown.value = false;
        isReplicationActive.value = false;
        return;
      }

      if (lastFinishedAtShown.value === finishedAt) {
        replicationMessageShown.value = false;
        isReplicationActive.value = false;
        return;
      }

      message.success('Копирование файлов успешно завершено');
      lastFinishedAtShown.value = finishedAt;
      replicationMessageShown.value = false;
      isReplicationActive.value = false;
    }
  },
  { immediate: false, deep: false },
);

onUnmounted(resetReplicationFeedback);

const changeName = (name) => {
  store.dispatch('crm/changePlanName', { id: planId.value, name }).finally(loadData);
};

const attachTagsToPlan = (tagsIds) => {
  loading.value = true;
  store.dispatch('crm/attachTagsToPlan', { id: planId.value, tagsIds }).finally(loadData);
};

const onButtonClick = (action) => {
  executeAction(action, planId.value, PlanType.Type2d, loadData, router, planData.value.status);
};

// loadData() уже вызван watch(planId, { immediate: true }) выше.
onMounted(() => {
  store.dispatch('crm/getTagsList');
  store.dispatch('crm/getSimpleUsersList');
});

const tagsList = computed(() => store.state.crm.tagsList);
const type = PlanType.Type2d;

store.dispatch('crm/getAdjustmentsByCrmPlanId', { id: planId.value });

const adjustments = computed(() => store.state.crm.adjustments.items);

const isImage = (url) => /\.(svg|webp|jpe?g|png)$/i.test(url);
const getFileExtension = (url) => {
  const matchResult = url.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
  return matchResult ? matchResult[1] : '';
};

const canChangeAssignees = computed(() => store.getters['auth/hasPermission']('ui.action.pages_crm_plans_id.1')
  || (store.getters['auth/hasPermission']('ui.action.pages_crm_plans_id.4')
    && plan.value?.manager?.id === authUser.value.id));

const canChangeNinja = computed(() => (
  canChangeAssignees.value || store.getters['auth/hasPermission']('ui.action.pages_crm_plans_id.8')
));

const canChangeReviewer = computed(() => store.getters['auth/hasPermission'](
  'ui.action.pages_crm_plans_id.1',
) || store.getters['auth/hasPermission'](
  'ui.action.pages_crm_plans_id.4',
));

const isSourcesArchiveCreating = computed(() => store.state.crm.apartments.isSourcesArchiveCreating);
const downloadSources = () => store.dispatch('crm/apartments/downloadSourcesArchive', planData.value?.apartment_id);

const compareCarousel = computed(() => ({
  originals: plan.value?.pictures || [],
  svgs: plan.value?.links?.svg_editor?.jpg || [],
  topViews: [],
}));

</script>

<style>
.ant-timeline-item-last {
  padding-bottom: 5px;
}

.ant-timeline-item-last > .ant-timeline-item-content {
  min-height: 0;
}

.status-history__user {
  display: block;
  width: auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

#plan-main {
  align-items: flex-start;
}

.plan-main-image {
  width: calc(100% / 3);
}

@media (max-width: 767px) {
  .plan-main-image {
    width: 100%;
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.ant-modal {
  top: 0 !important;
  max-width: 100% !important;
}

.plan-page__guards-hint {
  margin: 12px 0 0;
  font-size: 12px;
  color: #8a8a8a;
}

</style>
